import { useNavigate } from "react-router-dom";
import { useState, useEffect, } from "react";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Plus, Trash2 } from "lucide-react";
import { nanoid } from "nanoid";
import {
	calculateSeaShippingCost,
	calculateAirShippingCost,
	customerService,
	pricingTierService,
	settingsService,
	shipmentService,
	promoService,
	discountService,
} from "@/lib/database";
import type {
	Category,
	Customer,
	Discount,
	DiscountCustomerUsage,
	InsertCustomer,
	InsertShipment,
	PackageItem,
	PricingTier,
	PromoCode,
	PromoCodeUsage,
	Settings,
	Shipment,
	ShipmentDiscount,
} from "@/lib/supabase";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";
import { supabase } from "@/lib/supabase";
import RSelect from "react-select";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuPortal,
	DropdownMenuSeparator,
	DropdownMenuSub,
	DropdownMenuSubContent,
	DropdownMenuSubTrigger,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import uniqolor from "uniqolor";
import CostBreakdown from "@/components/admin/shipment/CostBreakdown";
import * as Sentry from '@sentry/browser'
import { handleError } from "@/lib/errors";
import { FunctionsFetchError, FunctionsHttpError, FunctionsRelayError } from "@supabase/supabase-js";

type ShipmentPackageItemForm = Omit<
	PackageItem,
	"id" | "shipment_package_id" | "created_at"
>;

type WeightSelection = "dimensional" | "scale" | "none";

type ShipmentPackageForm = {
	id: string;
	length: string;
	width: string;
	height: string;
	dimensional_weight: number | null;
	scale_weight: string;
	weight_preference: WeightSelection;
	items: ShipmentPackageItemForm[];
};

const createEmptyItem = (shippingMethod: string): ShipmentPackageItemForm => ({
	item_name: "",
	item_description: "",
	quantity: 1,
	total_weight: 0,
	total_value: 0,
	category_id: "",
	is_weight_included: shippingMethod === "air",
});

const createEmptyPackage = (shippingMethod: string): ShipmentPackageForm => ({
	id: nanoid(),
	length: "",
	width: "",
	height: "",
	dimensional_weight: null,
	scale_weight: "",
	weight_preference: shippingMethod === "sea" ? "dimensional" : "scale",
	items: [createEmptyItem(shippingMethod)],
});

type NewCustomer = Omit<
	InsertCustomer,
	"account_status" | "created_at" | "customer_tier" | "date_of_birth"
> & {
	createAuth: boolean;
};

export default function CreateShipment() {
	const [packages, setPackages] = useState<ShipmentPackageForm[]>([
		createEmptyPackage("air"),
	]);

	// New shipment form state
	const [newShipment, setNewShipment] = useState<InsertShipment>({
		admin_id: localStorage.getItem("userId")!,
		customer_id: "",
		route: "us_to_ht",
		shipping_method: "air",
		special_instructions: "",
		delivery_fee: 0,
		pickup_fee: 0,
		extra_charge: 0,
		total_cost: 0,
		is_pickup_requested: false,
		is_delivery_requested: false,
		pickup_address: "",
		delivery_address: "",
		total_weight: 0,
		is_express: false,
		express_fee: 0,
		base_freight_cost: 0,
		customs_tax_amount: 0,
		customs_tax_type: null,
		customs_tax_rate: null,
		discount_amount: 0,
		sub_total: 0,
		tracking_number: "",
	});

	// New customer form state
	const [newCustomer, setNewCustomer] = useState<NewCustomer>({
		first_name: "",
		last_name: "",
		email: "",
		phone: "",
		us_address: "",
		haiti_address: "",
		preferred_language: "english",
		notification_method: "email",
		preferred_shipping_method: "air",
		createAuth: true,
	});
	const [customers, setCustomers] = useState<Customer[]>([]);
	const [_, setLoading] = useState(true);
	const [isCreateCustomerOpen, setIsCreateCustomerOpen] = useState(false);
	const [pricingTiers, setPricingTiers] = useState<PricingTier[]>([]);
	const [seaRatePerCft, setSeaRatePerCft] = useState(20.0);
	const [categories, setCategories] = useState<Category[]>([]);
	const [isCustomsTaxesIncluded, setIsCustomsTaxesIncluded] = useState(false);

	const [activePromoCodes, setActivePromoCodes] = useState<PromoCode[]>([]);
	const [selectedCodes, setSelectedCodes] = useState<
		readonly { value: string; label: string }[]
	>([]);
	const [customerPromoUsages, setCustomerPromoUsages] = useState<
		PromoCodeUsage[]
	>([]);

	const [shipmentDiscounts, setShipmentDiscounts] = useState<
		Partial<ShipmentDiscount>[]
	>([]);
	const [activeDiscounts, setActiveDiscounts] = useState<Discount[]>([]);
	const [customerDiscountUsages, setCustomerDiscountUsages] = useState<
		DiscountCustomerUsage[]
	>([]);

	useEffect(() => {
		loadData();
	}, []);

	useEffect(() => {
		setShipmentDiscounts(
			selectedCodes.map((selectedPromo, idx) => {
				const discount = activeDiscounts.find(
					(code) => code.id === selectedPromo.value
				);
				const promo = activePromoCodes.find(
					(code) => code.id === selectedPromo.value
				);

				return {
					promo_id: promo?.id,
					discount_id: discount?.id,
					applied_order: idx + 1,
					promo,
					discount,
				};
			})
		);
	}, [selectedCodes]);

	const loadData = async () => {
		try {
			setLoading(true);

			// TODO: Only load last 10 and allow searching in the select box
			// Load all customers for dropdown
			const { customers: customerData } = await customerService.getAll(1000);

			const seaRatePerCft: Settings = await settingsService.getByKey(
				"sea_rate_per_cft"
			);
			const expressShippingFeeSettings: Settings =
				await settingsService.getByKey("express_shipping_fee");
			const pricingTiers: PricingTier[] = await pricingTierService.getAll();
			const categoriesData: Category[] =
				await shipmentService.getAllCategories();

			const { items: discounts } = await discountService.getAllActive(1000);
			const { items: promoCodes } = await promoService.getAllActive(1000);

			setActiveDiscounts(discounts);
			setActivePromoCodes(promoCodes);

			setPricingTiers(pricingTiers);
			setSeaRatePerCft(parseFloat(seaRatePerCft.value));
			setCategories(categoriesData);

			setNewShipment((prev) => ({
				...prev,
				express_fee: parseFloat(expressShippingFeeSettings.value),
			}));

			setCustomers(customerData || []);
		} catch (error) {
			console.error("Error loading data:", error);
		} finally {
			setLoading(false);
		}
	};

	const computeDimensionalWeight = (
		length: string,
		width: string,
		height: string
	) => {
		const l = parseFloat(length);
		const w = parseFloat(width);
		const h = parseFloat(height);

		if (!l || !w || !h) return null;

		return Number(((l * w * h) / 166).toFixed(2));
	};

	const getPackageCostWeight = (pkg: ShipmentPackageForm) => {
		if (pkg.weight_preference === "none") return 0;

		if (pkg.weight_preference === "dimensional") {
			return pkg.dimensional_weight || 0;
		}

		return parseFloat(pkg.scale_weight) || 0;
	};

	const autoSelectWeightPreference = (
		pkg: ShipmentPackageForm,
		shippingMethod: string
	) => {
		if (shippingMethod === "sea") return "dimensional";

		const dimensional = pkg.dimensional_weight || 0;
		const scale = parseFloat(pkg.scale_weight) || 0;

		if (!dimensional && !scale) return "none";

		return dimensional > scale ? "dimensional" : "scale";
	};

	useEffect(() => {
		setPackages((prev) =>
			prev.map((pkg) => ({
				...pkg,
				weight_preference: autoSelectWeightPreference(
					pkg,
					newShipment.shipping_method
				),
			}))
		);
	}, [newShipment.shipping_method]);

	useEffect(() => {
		const totalWeight = packages.reduce(
			(sum, pkg) => sum + getPackageCostWeight(pkg),
			0
		);
		setNewShipment((prev) => ({
			...prev,
			total_weight: totalWeight,
		}));
	}, [packages]);

	const addPackage = () => {
		setPackages((prev) => [
			createEmptyPackage(newShipment.shipping_method),
			...prev,
		]);
	};

	const removePackage = (index: number) => {
		setPackages((prev) => prev.filter((_, i) => i !== index));
	};

	const updatePackageFields = (
		index: number,
		fields: Partial<ShipmentPackageForm>
	) => {
		setPackages((prev) => {
			const updated = [...prev];
			const nextPackage = { ...updated[index], ...fields };
			nextPackage.dimensional_weight = computeDimensionalWeight(
				nextPackage.length,
				nextPackage.width,
				nextPackage.height
			);
			nextPackage.weight_preference = autoSelectWeightPreference(
				nextPackage,
				newShipment.shipping_method
			);
			updated[index] = nextPackage;
			return updated;
		});
	};

	const updatePackageWeightPreference = (
		index: number,
		value: WeightSelection
	) => {
		setPackages((prev) => {
			const updated = [...prev];
			updated[index] = { ...updated[index], weight_preference: value };
			return updated;
		});
	};

	const addPackageItem = (pkgIndex: number) => {
		setPackages((prev) => {
			const updated = [...prev];
			updated[pkgIndex] = {
				...updated[pkgIndex],
				items: [
					createEmptyItem(newShipment.shipping_method),
					...updated[pkgIndex].items,
				],
			};
			return updated;
		});
	};

	const removePackageItem = (pkgIndex: number, itemIndex: number) => {
		setPackages((prev) => {
			const updated = [...prev];
			updated[pkgIndex] = {
				...updated[pkgIndex],
				items: updated[pkgIndex].items.filter((_, i) => i !== itemIndex),
			};
			return updated;
		});
	};

	const updateShipmentItem = (
		pkgIndex: number,
		itemIndex: number,
		field: keyof ShipmentPackageItemForm,
		value: any
	) => {
		setPackages((prev) => {
			const updated = [...prev];
			const items = [...updated[pkgIndex].items];
			items[itemIndex] = { ...items[itemIndex], [field]: value };
			const updatedPackage = { ...updated[pkgIndex], items };
			updatedPackage.weight_preference = autoSelectWeightPreference(
				updatedPackage,
				newShipment.shipping_method
			);
			updated[pkgIndex] = updatedPackage;
			return updated;
		});
	};

	const updateShipmentItemKeys = (
		pkgIndex: number,
		itemIndex: number,
		fields: Record<string, any>
	) => {
		setPackages((prev) => {
			const updated = [...prev];
			const items = [...updated[pkgIndex].items];
			items[itemIndex] = { ...items[itemIndex], ...fields };
			const updatedPackage = { ...updated[pkgIndex], items };
			updatedPackage.weight_preference = autoSelectWeightPreference(
				updatedPackage,
				newShipment.shipping_method
			);
			updated[pkgIndex] = updatedPackage;
			return updated;
		});
	};

	const calculateShipmentCost = async () => {
		try {
			let baseFreightCost = 0;

			if (newShipment.shipping_method === "air") {
				const weight = packages.reduce(
					(sum, pkg) => sum + getPackageCostWeight(pkg),
					0
				);

				// if (!weight || !pricingTiers.length) {
				//   setNewShipment((prev) => ({
				//     ...prev,
				//     total_cost: 0,
				//     base_freight_cost: 0,
				//     discount_amount: 0,
				//     customs_tax_amount: 0,
				//     sub_total: 0,
				//   }));

				//   return;
				// }

				// Get air rate for this weight from pricing tiers
				const airRate =
					pricingTiers.find((tier) => tier.tier_order === 1)?.rate || 0;
				const weightTier = pricingTiers.find(
					(tier) =>
						weight >= tier.min_weight &&
						(!tier.max_weight || weight <= tier.max_weight)
				);

				if (weightTier) {
					baseFreightCost = calculateAirShippingCost(weight, weightTier.rate);
				} else {
					baseFreightCost = calculateAirShippingCost(weight, airRate);
				}
			} else {
				const seaCost = packages.reduce((sum, pkg) => {
					if (pkg.weight_preference === "none") return sum;

					const length = parseFloat(pkg.length);
					const width = parseFloat(pkg.width);
					const height = parseFloat(pkg.height);

					if (!length || !width || !height) return sum;

					return (
						sum + calculateSeaShippingCost(length, width, height, seaRatePerCft)
					);
				}, 0);

				baseFreightCost = seaCost;
			}

			const subTotal =
				baseFreightCost +
				(Number(newShipment.pickup_fee) || 0) +
				(Number(newShipment.delivery_fee) || 0) +
				(Number(newShipment.extra_charge) || 0) +
				((newShipment.is_express && Number(newShipment.express_fee)) || 0);

			// Calculate and apply discounts
			const { discount_amount, current: adjustedSubTotal } =
				shipmentDiscounts.reduce(
					(acc, code) => {
						const type = code.discount?.discount_type || code.promo?.promo_type;
						const amount = code.discount?.amount || code.promo?.amount || 0;

						const before = acc.current;

						const after =
							type === "percentage"
								? before - before * (amount / 100)
								: before - amount;

						acc.current = after;
						acc.discount_amount += before - after;
						return acc;
					},
					{ current: subTotal, discount_amount: 0 }
				);

			const customsTaxAmount =
				isCustomsTaxesIncluded && newShipment.customs_tax_rate
					? newShipment.customs_tax_type === "percentage"
						? adjustedSubTotal * (newShipment.customs_tax_rate / 100)
						: newShipment.customs_tax_rate
					: 0;

			const totalCost = adjustedSubTotal + customsTaxAmount;

			setNewShipment((prev) => ({
				...prev,
				total_cost: Math.round(totalCost * 100) / 100,
				base_freight_cost: Math.round(baseFreightCost * 100) / 100,
				customs_tax_amount: Math.round(customsTaxAmount * 100) / 100,
				discount_amount: Math.round(discount_amount * 100) / 100,
				sub_total: Math.round(subTotal * 100) / 100,
			}));
		} catch (error) {
			console.error("Error calculating cost:", error);
		}
	};

	useEffect(() => {
		calculateShipmentCost();
	}, [
		newShipment.shipping_method,
		newShipment.pickup_fee,
		newShipment.delivery_fee,
		newShipment.extra_charge,
		newShipment.is_express,
		newShipment.express_fee,
		newShipment.customs_tax_rate,
		newShipment.customs_tax_type,
		isCustomsTaxesIncluded,
		packages,
		shipmentDiscounts,
	]);

	const handleCreateShipment = async (status: Shipment["status"]) => {
		return await Sentry.withScope(async (scope) => {
			const userFullName = localStorage.getItem("userFullName");
			const userEmail = localStorage.getItem("userEmail");

			// Set the user who is performing the action
			// Assuming 'user' is your logged-in admin
			Sentry.setUser({
				id: userEmail || undefined,
				email: userEmail || undefined,
				username: userFullName || undefined,
			});

			try {
				// 1️⃣ Validation checks
				if (!newShipment.customer_id) {
					toast.error("Please select a customer", {
						position: "top-center",
						closeButton: true,
					});
					return;
				}

				if (packages.length === 0) {
					toast.error("Please add at least one package", {
						position: "top-center",
						closeButton: true,
					});
					return;
				}

				if (newShipment.shipping_method === "air") {
					const hasUsableWeight = packages.some(
						(pkg) =>
							pkg.weight_preference !== "none" && getPackageCostWeight(pkg) > 0
					);

					if (!hasUsableWeight) {
						toast.error(
							"Please enter a dimensional or scale weight for at least one package",
							{ position: "top-center", closeButton: true }
						);
						return;
					}
				}

				if (
					newShipment.shipping_method === "sea" &&
					packages.some((pkg) => !pkg.length || !pkg.width || !pkg.height)
				) {
					toast.error("Please enter dimensions for all sea shipment packages", {
						position: "top-center",
						closeButton: true,
					});
					return;
				}

				if (newShipment.is_pickup_requested && !newShipment.pickup_address) {
					toast.error("Please enter pick up address", {
						position: "top-center",
						closeButton: true,
					});
					return;
				}

				if (newShipment.is_delivery_requested && !newShipment.delivery_address) {
					toast.error("Please enter delivery address", {
						position: "top-center",
						closeButton: true,
					});
					return;
				}

				const itemsMissingCategory = packages.some((pkg) =>
					pkg.items.some((item) => item.item_name.trim() && !item.category_id)
				);

				if (itemsMissingCategory) {
					toast.error("Please make sure all shipment items have a category", {
						position: "top-center",
						closeButton: true,
					});
					return;
				}

				const packagePayloads = packages.map((pkg) => {
					const validItems = pkg.items.filter(
						(item) => item.item_name.trim() !== ""
					);

					const packageItems: Omit<
						PackageItem,
						"id" | "shipment_package_id" | "created_at"
					>[] = validItems.map((item) => ({
						item_name: item.item_name,
						item_description: item.item_description,
						quantity: item.quantity,
						total_weight: item.total_weight,
						total_value: item.total_value,
						category_id: item.category_id,
						is_weight_included: true,
					}));

					return {
						package: {
							scale_weight: pkg.scale_weight
								? parseFloat(pkg.scale_weight)
								: null,
							dimensional_weight: pkg.dimensional_weight ?? null,
							is_weight_included: pkg.weight_preference !== "none",
							dimensions:
								pkg.length && pkg.width && pkg.height
									? `${pkg.length}x${pkg.width}x${pkg.height}`
									: null,
						},
						items: packageItems,
					};
				});

				const allItems = packagePayloads.flatMap((pkg) => pkg.items);

				if (packagePayloads.some((pkg) => pkg.items.length === 0)) {
					toast.error("Each package must include at least one item with a name", {
						position: "top-center",
						closeButton: true,
					});
					return;
				}

				if (
					!allItems.length ||
					allItems.some((item) => item.item_name.trim() === "")
				) {
					toast.error("Please add at least one shipment item with a name", {
						position: "top-center",
						closeButton: true,
					});
					return;
				}

				let createdShipment;

				// Attach the form data so it shows up in the Sentry "Context" tab
				scope.setContext("Shipment Creation Data", {
					packagePayloads,
					newShipment,
				});

				try {
					createdShipment = await shipmentService.create(
						{ ...newShipment, status },
						packagePayloads
					);
				} catch (shipmentError: any) {
					console.error("Shipment creation error:", shipmentError);
					toast.error(
						"Failed to create shipment. Please check your data and try again.",
						{
							position: "top-center",
							closeButton: true,
						}
					);

					throw shipmentError;
				}

				// 4.a Register Shipment discounts and usages
				if (shipmentDiscounts.length) {

					// Attach the form data so it shows up in the Sentry "Context" tab
					scope.setContext("Shipment Discount Registration Data", {
						shipmentDiscounts,
					});

					try {
						// Upon usage insert, Postgres handles creating/incrementing usages through
						// a trigger named trg_apply_code_usage
						await shipmentService.createShipmentDiscounts(
							shipmentDiscounts.map((code) => {
								return {
									promo_id: code.promo_id || null,
									discount_id: code.discount_id || null,
									applied_order: code.applied_order,
									shipment_id: createdShipment.id,
								};
							})
						);
					} catch (discountRegistrationError: any) {
						console.error(
							"Discount registration error:",
							discountRegistrationError
						);

						toast.error(
							"Failed to create shipment. Please check your data and try again.",
							{
								position: "top-center",
								closeButton: true,
							}
						);

						throw discountRegistrationError;
					}
				}

				// 5️⃣ Send email notification
				// TODO: Move to shipment service and ultimately set up a more optimized mechanism.
				// such as a queue table polled by a cron job + edge function
				try {
					const { error } = await supabase.functions.invoke("send-email", {
						method: "POST",
						body: JSON.stringify({
							data: {
								shipment: createdShipment,
								customer: customers.find((c) => c.id === newShipment.customer_id),
							},
							type: "new_shipment",
						}),
					});

					if (error) {
						throw new Error(error.message || "Failed to send shipment email");
					}
				} catch (emailError: any) {
					console.error("Email sending error:", emailError);
					toast.error(
						"Shipment created, but failed to send email notification.",
						{
							position: "top-center",
							closeButton: true,
						}
					);

					throw emailError;
				}

				// 6️⃣ Success
				toast.success("Shipment created successfully!", {
					position: "top-center",
				});

				// resetShipmentForm();
				navigate(`/admin/shipments/${createdShipment.tracking_number}`);
			} catch (error) {
				console.error("Unexpected error:", error);
				toast.error("An unexpected error occurred. Please try again.", {
					position: "top-center",
					closeButton: true,
				});

				Sentry.captureException(error);
			}
		})
	};

	const handleCreateCustomer = async () => {
		// Start a span for the entire operation
		return await Sentry.startSpan({
			name: "Quickly Create Customer Workflow",
			op: "ui.workflow",
		}, async () => {
			if (
				!newCustomer.first_name ||
				!newCustomer.last_name ||
				!newCustomer.email
			) {
				alert("Please fill in all required fields");
				return;
			}

			const { createAuth, ...customerData } = newCustomer;
			// const customer = await customerService.createWithNotification(customerData, createAuth, notificationMethod);

			let customer: Customer;

			if (createAuth) {
				// TODO: Move this into the customer service
				const { error: invokeError, data } = await supabase.functions.invoke("create-user", {
					method: "POST",
					headers: {
						// Manually inject the current trace context into headers
						...Sentry.getTraceData(),
					},
					body: JSON.stringify({
						user: customerData,
						notificationMethod: newCustomer.notification_method,
						userType: "customer",
					}),
				});

				let errorPayload = null;

				// FunctionsHttpError: Your function executed but returned an error (4xx/5xx status)
				// FunctionsRelayError: Network issue between client and Supabase
				// FunctionsFetchError: Function couldn't be reached at all
				// Docs: https://supabase.com/docs/guides/functions/error-handling#client-side-error-handling

				if (invokeError && invokeError instanceof FunctionsHttpError) {
					errorPayload = await invokeError.context.json()
				} else if (invokeError && invokeError instanceof FunctionsRelayError) {
					errorPayload = { message: invokeError.message }
				} else if (invokeError && invokeError instanceof FunctionsFetchError) {
					errorPayload = { message: invokeError.message }
				}

				if (errorPayload) {
					// Now we have a unified error object regardless of the status code
					alert(await handleError(errorPayload));
					return;
				}

				customer = data.insertedUser;
			} else {
				customer = await customerService.create(customerData, false);
			}

			setIsCreateCustomerOpen(false);
			setNewCustomer({
				first_name: "",
				last_name: "",
				email: "",
				phone: "",
				us_address: "",
				haiti_address: "",
				preferred_language: "english",
				notification_method: "email",
				preferred_shipping_method: "air",
				createAuth: true,
			});

			// Add to customers list and select for shipment
			setCustomers((prev) => [customer, ...prev]);
			setNewShipment((prev) => ({ ...prev, customer_id: customer.id }));

			if (createAuth) {
				alert(
					`Customer account created successfully! Login credentials have been sent via ${newCustomer.notification_method === "both"
						? "both email and sms"
						: newCustomer.notification_method
					}.`
				);
			} else {
				alert("Customer profile created successfully!");
			}
		})
	};

	const navigate = useNavigate();

	// if (loading) {
	//   return (
	//     <AdminLayout>
	//       <div className="flex items-center justify-center h-64">
	//         <div className="text-lg text-gray-600">Loading...</div>
	//       </div>
	//     </AdminLayout>
	//   );
	// }

	useEffect(() => {
		const getCustomerUsages = async () => {
			if (!newShipment.customer_id) return;

			const promoUsages = await promoService.getCustomerUsages(
				newShipment.customer_id
			);
			setCustomerPromoUsages(promoUsages);

			const discountUsages = await discountService.getCustomerUsages(
				newShipment.customer_id
			);
			setCustomerDiscountUsages(discountUsages);
		};

		getCustomerUsages();
	}, [newShipment.customer_id, activeDiscounts]);

	useEffect(() => {
		setNewShipment((prev) => ({
			...prev,
			customs_tax_rate: isCustomsTaxesIncluded ? 10 : null,
			customs_tax_type: isCustomsTaxesIncluded ? "percentage" : null,
		}));
	}, [isCustomsTaxesIncluded]);

	return (
		<div className="space-y-6">
			{/* Header */}
			<div className="flex items-center justify-between">
				<div className="flex space-x-4">
					<Button variant="ghost" onClick={() => navigate(-1)}>
						<ArrowLeft className="h-4 w-4 mr-2" />
						Cancel
					</Button>
					<div>
						<h1 className="text-3xl font-bold text-gray-900">
							Create New Shipment
						</h1>
						<p className="text-gray-600">
							Fill in the details to create a new shipment with packages and
							items
						</p>
					</div>
				</div>

				{/* <Button onClick={handle} disabled={saving} className="bg-[#254164] hover:bg-[#1a2d47]">
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Saving...' : 'Save Changes'}
          </Button> */}
			</div>

			<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
				{/* Left Column */}
				<div className="space-y-2">
					{/* Shipment Information */}
					<Card>
						<CardHeader>
							<CardTitle>Shipment Information</CardTitle>
							<CardDescription>Basic shipment details</CardDescription>
						</CardHeader>
						<CardContent className="space-y-4">
							<div className="space-y-2">
								<Label htmlFor="route">Transit Route *</Label>
								<Select
									value={newShipment.route}
									onValueChange={(value: "us_to_ht" | "ht_to_us") =>
										setNewShipment((prev) => ({ ...prev, route: value }))
									}
								>
									<SelectTrigger>
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="us_to_ht">USA to Haiti</SelectItem>
										<SelectItem value="ht_to_us">Haiti to USA</SelectItem>
									</SelectContent>
								</Select>
							</div>

							<div className="space-y-2">
								<Label htmlFor="shipping_method">Shipping Method *</Label>
								<Select
									value={newShipment.shipping_method}
									onValueChange={(value: "air" | "sea") =>
										setNewShipment((prev) => ({
											...prev,
											shipping_method: value,
										}))
									}
								>
									<SelectTrigger>
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="air">
											Air Shipping (Weight-based)
										</SelectItem>
										<SelectItem value="sea">
											Sea Shipping (Volume-based)
										</SelectItem>
									</SelectContent>
								</Select>
							</div>

							<div className="space-y-2">
								<Label htmlFor="special_instructions">
									Special Instructions
								</Label>
								<Textarea
									id="special_instructions"
									value={newShipment.special_instructions!}
									onChange={(e) =>
										setNewShipment((prev) => ({
											...prev,
											special_instructions: e.target.value,
										}))
									}
								/>
							</div>
						</CardContent>
					</Card>
				</div>

				{/* Right Column */}
				<div className="space-y-2">
					{/* Customer Information */}
					<Card>
						<CardHeader>
							<CardTitle>Customer Information</CardTitle>
							<CardDescription>Details about the customer</CardDescription>
						</CardHeader>
						<CardContent className="space-y-2">
							<div className="space-y-4">
								<div className="space-y-2">
									<Label htmlFor="customer">Customer *</Label>
									<div className="flex space-x-2 items-center">
										<Select
											value={newShipment.customer_id!}
											onValueChange={(value) =>
												setNewShipment((prev) => ({
													...prev,
													customer_id: value,
												}))
											}
										>
											<SelectTrigger className="flex-1">
												<SelectValue placeholder="Search and select customer" />
											</SelectTrigger>
											<SelectContent>
												{Array.isArray(customers) &&
													customers.map((customer) => (
														<SelectItem key={customer.id} value={customer.id}>
															{customer.first_name} {customer.last_name} -{" "}
															{customer.email}
														</SelectItem>
													))}
											</SelectContent>
										</Select>

										<Dialog
											open={isCreateCustomerOpen}
											onOpenChange={setIsCreateCustomerOpen}
										>
											<DialogTrigger asChild>
												<Button variant="outline" size="sm">
													<Plus className="w-4" />
												</Button>
											</DialogTrigger>

											<DialogContent className="max-h-[90vh] overflow-y-auto">
												<DialogHeader>
													<DialogTitle>Add New Customer</DialogTitle>
												</DialogHeader>
												<div className="space-y-4">
													<div className="grid grid-cols-2 gap-4">
														<div>
															<Label htmlFor="first_name">First Name *</Label>
															<Input
																id="first_name"
																value={newCustomer.first_name}
																onChange={(e) =>
																	setNewCustomer((prev) => ({
																		...prev,
																		first_name: e.target.value,
																	}))
																}
															/>
														</div>
														<div>
															<Label htmlFor="last_name">Last Name *</Label>
															<Input
																id="last_name"
																value={newCustomer.last_name}
																onChange={(e) =>
																	setNewCustomer((prev) => ({
																		...prev,
																		last_name: e.target.value,
																	}))
																}
															/>
														</div>
													</div>
													<div>
														<Label htmlFor="email">Email *</Label>
														<Input
															id="email"
															type="email"
															value={newCustomer.email}
															onChange={(e) =>
																setNewCustomer((prev) => ({
																	...prev,
																	email: e.target.value,
																}))
															}
														/>
													</div>
													<div>
														<Label htmlFor="phone">Phone</Label>
														<Input
															id="phone"
															value={newCustomer.phone!}
															onChange={(e) =>
																setNewCustomer((prev) => ({
																	...prev,
																	phone: e.target.value,
																}))
															}
														/>
													</div>
													<div>
														<Label htmlFor="us_address">US Address</Label>
														<Input
															id="us_address"
															value={newCustomer.us_address!}
															onChange={(e) =>
																setNewCustomer((prev) => ({
																	...prev,
																	us_address: e.target.value,
																}))
															}
														/>
													</div>
													<div>
														<Label htmlFor="haiti_address">Haiti Address</Label>
														<Input
															id="haiti_address"
															value={newCustomer.haiti_address!}
															onChange={(e) =>
																setNewCustomer((prev) => ({
																	...prev,
																	haiti_address: e.target.value,
																}))
															}
														/>
													</div>
													<div>
														<Label htmlFor="preferred_language">
															Preferred Language
														</Label>
														<Select
															value={newCustomer.preferred_language!}
															onValueChange={(
																value: "english" | "french" | "creole"
															) =>
																setNewCustomer((prev) => ({
																	...prev,
																	preferred_language: value,
																}))
															}
														>
															<SelectTrigger>
																<SelectValue />
															</SelectTrigger>
															<SelectContent>
																<SelectItem value="english">English</SelectItem>
																<SelectItem value="french">French</SelectItem>
																<SelectItem value="creole">Creole</SelectItem>
															</SelectContent>
														</Select>
													</div>
													<div className="flex items-center space-x-2">
														<Switch
															id="create-auth"
															checked={newCustomer.createAuth}
															onCheckedChange={(checked) =>
																setNewCustomer((prev) => ({
																	...prev,
																	createAuth: checked,
																}))
															}
														/>
														<Label htmlFor="create-auth">
															Create login account and send credentials
														</Label>
													</div>
													{newCustomer.createAuth && (
														<div className="space-y-2">
															<Label>Send credentials via:</Label>
															<RadioGroup
																value={newCustomer.notification_method}
																onValueChange={(value: "email" | "sms") =>
																	setNewCustomer((prev) => ({
																		...prev,
																		notification_method: value,
																	}))
																}
															>
																<div className="flex items-center space-x-2">
																	<RadioGroupItem
																		value="email"
																		id="email-notification"
																	/>
																	<Label htmlFor="email-notification">
																		Email
																	</Label>
																</div>
																<div className="flex items-center space-x-2">
																	<RadioGroupItem
																		disabled
																		value="sms"
																		id="sms-notification"
																	/>
																	<Label htmlFor="sms-notification">
																		Text Message
																	</Label>
																</div>
															</RadioGroup>
														</div>
													)}
													<Button
														onClick={handleCreateCustomer}
														className="w-full"
													>
														Create Customer
													</Button>
												</div>
											</DialogContent>
										</Dialog>
									</div>
								</div>
								<div className="space-x-2 items-center flex">
									<Checkbox
										id="is_pickup_requested"
										checked={newShipment.is_pickup_requested!}
										onCheckedChange={(checked: boolean) =>
											setNewShipment((prev) => ({
												...prev,
												is_pickup_requested: checked,
											}))
										}
									/>
									<Label htmlFor="is_pickup_requested">Pickup Requested</Label>
								</div>

								{newShipment.is_pickup_requested && (
									<div>
										<Label>Pickup Address</Label>
										<Input
											type="text"
											value={newShipment.pickup_address!}
											onChange={(e) =>
												setNewShipment((prev) => ({
													...prev,
													pickup_address: e.target.value,
												}))
											}
										/>
									</div>
								)}

								<div className="space-x-2 items-center flex">
									<Checkbox
										id="is_delivery_requested"
										checked={newShipment.is_delivery_requested!}
										onCheckedChange={(checked: boolean) =>
											setNewShipment((prev) => ({
												...prev,
												is_delivery_requested: checked,
											}))
										}
									/>
									<Label htmlFor="is_delivery_requested">
										Delivery Requested
									</Label>
								</div>

								{newShipment.is_delivery_requested && (
									<div>
										<Label>Delivery Address</Label>
										<Input
											type="text"
											value={newShipment.delivery_address!}
											onChange={(e) =>
												setNewShipment((prev) => ({
													...prev,
													delivery_address: e.target.value,
												}))
											}
										/>
									</div>
								)}

								{newShipment.shipping_method === "air" && (
									<div className="space-y-4">
										<div className="space-x-2 items-center flex">
											<Checkbox
												id="is_express"
												checked={newShipment.is_express!}
												onCheckedChange={(checked: boolean) =>
													setNewShipment((prev) => ({
														...prev,
														is_express: checked,
													}))
												}
											/>
											<Label htmlFor="is_express">
												Express Shipping Requested
											</Label>
										</div>

										{newShipment.is_express && (
											<div>
												<Label>Express Fee</Label>
												<div className="relative">
													<span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">
														$
													</span>

													<Input
														id="express_fee"
														type="number"
														value={newShipment.express_fee || ""}
														min={0}
														onChange={(e) =>
															setNewShipment((prev) => ({
																...prev,
																express_fee:
																	parseFloat(e.target.value) || undefined,
															}))
														}
														className="pl-8"
													/>
												</div>
											</div>
										)}
									</div>
								)}
							</div>
						</CardContent>
					</Card>
				</div>
			</div>

			<div className="space-y-2">
				<Card>
					<CardHeader>
						<CardTitle>Packages</CardTitle>
						<CardDescription>
							Packages and individual items in each package
						</CardDescription>
					</CardHeader>

					<CardContent className="space-y-6">
						<div className="flex items-center justify-between">
							<Label className="text-lg font-medium">Packages</Label>

							<Button
								type="button"
								variant="outline"
								size="sm"
								onClick={addPackage}
							>
								<Plus className="h-4 w-4 mr-2" />
								Add Package
							</Button>
						</div>

						<div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
							{packages.map((pkg, pkgIndex) => {
								const { color, isLight } = uniqolor(pkg.id, {
									lightness: 25,
								});

								return (
									<div
										key={pkg.id}
										className="overflow-hidden bg-gray-50 rounded-lg shadow-sm"
									>
										<div
											style={{
												backgroundColor: color,
												color: isLight ? "black" : "white",
											}}
											className={`sticky top-0 z-10 flex items-center justify-between px-4 py-3 border-b`}
										>
											<h4 className="font-medium">
												Package {packages.length - pkgIndex}
											</h4>
											{packages.length > 1 && (
												<Button
													type="button"
													variant="ghost"
													size="sm"
													onClick={() => removePackage(pkgIndex)}
												>
													<Trash2 className="h-4 w-4" />
												</Button>
											)}
										</div>

										<div className="max-h-[438px] border-l border-r overflow-y-auto space-y-4 px-4 pb-4">
											<div className="sticky top-0 z-10 bg-gray-50 my-2 py-2 flex items-center justify-between">
												<Label className="font-medium">Items</Label>

												<Button
													type="button"
													variant="outline"
													size="sm"
													onClick={() => addPackageItem(pkgIndex)}
												>
													<Plus className="h-4 w-4 mr-2" />
													Add Item
												</Button>
											</div>

											{pkg.items.map((item, itemIndex) => (
												<div
													key={itemIndex}
													className="space-y-4 rounded-md border border-gray-200 bg-white p-4"
												>
													<div className="flex items-center justify-between">
														<h5 className="font-medium">
															Item {pkg.items.length - itemIndex}
														</h5>
														{pkg.items.length > 1 && (
															<Button
																type="button"
																variant="ghost"
																size="sm"
																onClick={() =>
																	removePackageItem(pkgIndex, itemIndex)
																}
															>
																<Trash2 className="h-4 w-4 text-destructive" />
															</Button>
														)}
													</div>

													<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
														<div>
															<Label>Item Name</Label>
															<Input
																value={item.item_name}
																onChange={(e) =>
																	updateShipmentItem(
																		pkgIndex,
																		itemIndex,
																		"item_name",
																		e.target.value
																	)
																}
																placeholder="e.g., Electronics, Clothing"
															/>
														</div>
														<div>
															<Label>Category</Label>
															<RSelect
																options={categories.map((cat) => ({
																	value: cat.id,
																	label: cat.name,
																}))}
																value={
																	item.category_id
																		? {
																			label: categories.find(
																				(cat) => cat.id === item.category_id
																			)?.name,
																			value: item.category_id,
																		}
																		: null
																}
																onChange={(cat) => {
																	if (!cat) return;

																	updateShipmentItemKeys(pkgIndex, itemIndex, {
																		category_id: cat.value,
																	});
																}}
																placeholder="e.g., Electronics, Apparel"
															/>
														</div>
													</div>

													<div>
														<Label>Description</Label>
														<Input
															value={item.item_description || ""}
															onChange={(e) =>
																updateShipmentItem(
																	pkgIndex,
																	itemIndex,
																	"item_description",
																	e.target.value
																)
															}
															placeholder="Detailed description of the item"
														/>
													</div>

													<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
														<div>
															<Label>Quantity</Label>
															<Input
																type="number"
																value={item.quantity || ""}
																onChange={(e) =>
																	updateShipmentItem(
																		pkgIndex,
																		itemIndex,
																		"quantity",
																		parseInt(e.target.value) || 0
																	)
																}
															/>
														</div>
														<div>
															<Label>Total Weight (lbs)</Label>
															<Input
																type="number"
																value={item.total_weight || ""}
																onChange={(e) =>
																	updateShipmentItem(
																		pkgIndex,
																		itemIndex,
																		"total_weight",
																		parseFloat(e.target.value) || undefined
																	)
																}
															/>
														</div>
														<div>
															<Label>Total Value ($)</Label>
															<Input
																type="number"
																value={item.total_value || ""}
																onChange={(e) =>
																	updateShipmentItem(
																		pkgIndex,
																		itemIndex,
																		"total_value",
																		parseFloat(e.target.value) || undefined
																	)
																}
															/>
														</div>
													</div>
												</div>
											))}
										</div>

										<div
											style={{
												backgroundColor: color,
												color: isLight ? "black" : "white",
											}}
											className="sticky bottom-0 z-10 border-t text-white px-4 py-4 space-y-3"
										>
											<h4 className="font-medium">
												Package {packages.length - pkgIndex} Measurements
											</h4>

											<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
												<div className="space-y-2">
													<Label htmlFor={`length-${pkg.id}`}>
														Length (inches)
													</Label>
													<Input
														className="text-black"
														id={`length-${pkg.id}`}
														type="number"
														value={pkg.length}
														onChange={(e) =>
															updatePackageFields(pkgIndex, {
																length: e.target.value,
															})
														}
													/>
												</div>
												<div className="space-y-2">
													<Label htmlFor={`width-${pkg.id}`}>
														Width (inches)
													</Label>
													<Input
														className="text-black"
														id={`width-${pkg.id}`}
														type="number"
														value={pkg.width}
														onChange={(e) =>
															updatePackageFields(pkgIndex, {
																width: e.target.value,
															})
														}
													/>
												</div>
												<div className="space-y-2">
													<Label htmlFor={`height-${pkg.id}`}>
														Height (inches)
													</Label>
													<Input
														className="text-black"
														id={`height-${pkg.id}`}
														type="number"
														value={pkg.height}
														onChange={(e) =>
															updatePackageFields(pkgIndex, {
																height: e.target.value,
															})
														}
													/>
												</div>
											</div>

											<p className="text-xs">
												<span className="font-bold">Volume:</span>{" "}
												{pkg.length && pkg.width && pkg.height
													? `${Number(pkg.length) *
													Number(pkg.width) *
													Number(pkg.height)
													} in³`
													: "Enter all dimensions to calculate"}
											</p>

											<p className="text-sm">
												<span className="font-bold">Volume Weight:</span>{" "}
												{pkg.dimensional_weight
													? `${pkg.dimensional_weight} lbs`
													: "Enter all dimensions to calculate"}
											</p>

											{newShipment.shipping_method === "air" && (
												<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
													<div className="space-y-2 md:col-start-1">
														<Label htmlFor={`scale-weight-${pkg.id}`}>
															Scale Weight (lbs)
														</Label>
														<Input
															className="text-black"
															id={`scale-weight-${pkg.id}`}
															type="number"
															value={pkg.scale_weight}
															onChange={(e) =>
																updatePackageFields(pkgIndex, {
																	scale_weight: e.target.value,
																})
															}
														/>
													</div>
												</div>
											)}

											<div className="space-y-2">
												<Label className="text-sm font-medium">
													Weight used for cost calculation
												</Label>
												<RadioGroup
													value={pkg.weight_preference}
													onValueChange={(value) =>
														updatePackageWeightPreference(
															pkgIndex,
															value as WeightSelection
														)
													}
													className="grid gap-2 sm:grid-cols-3"
												>
													<div className="flex items-center space-x-2">
														<RadioGroupItem
															style={{
																backgroundColor: isLight ? "black" : "white",
															}}
															value="dimensional"
															id={`dimensional-${pkg.id}`}
														/>
														<Label
															className="text-sm"
															htmlFor={`dimensional-${pkg.id}`}
														>
															Volume Weight
														</Label>
													</div>
													<div className="flex items-center space-x-2">
														<RadioGroupItem
															value="scale"
															style={{
																backgroundColor: isLight ? "black" : "white",
															}}
															id={`scale-${pkg.id}`}
															disabled={newShipment.shipping_method === "sea"}
														/>
														<Label
															className="text-sm"
															htmlFor={`scale-${pkg.id}`}
														>
															Scale Weight
														</Label>
													</div>
													<div className="flex items-center space-x-2">
														<RadioGroupItem
															style={{
																backgroundColor: isLight ? "black" : "white",
															}}
															value="none"
															id={`none-${pkg.id}`}
														/>
														<Label
															className="text-sm"
															htmlFor={`none-${pkg.id}`}
														>
															Do not include in calculation
														</Label>
													</div>
												</RadioGroup>
											</div>
										</div>
									</div>
								);
							})}
						</div>
					</CardContent>
				</Card>
			</div>

			<div className="space-y-2">
				<Card>
					<CardHeader>
						<CardTitle>Pricing Information</CardTitle>
						<CardDescription>
							Cost breakdown, discounts, and fees
						</CardDescription>
					</CardHeader>

					<CardContent className="space-y-6">
						<div className="flex flex-col space-y-4 md:flex-row md:justify-between md:space-x-6 md:space-y-0">
							{newShipment.is_pickup_requested && (
								<div className="space-y-2 w-full">
									<Label htmlFor="pickup_fee">PickUp Fee ($)</Label>
									<Input
										id="pickup_fee"
										type="number"
										value={newShipment.pickup_fee || ""}
										onChange={(e) =>
											setNewShipment((prev) => ({
												...prev,
												pickup_fee: parseFloat(e.target.value) || undefined,
											}))
										}
									/>
								</div>
							)}

							{newShipment.is_delivery_requested && (
								<div className="space-y-2 w-full">
									<Label htmlFor="delivery_fee">Delivery Fee ($)</Label>
									<Input
										id="delivery_fee"
										type="number"
										value={newShipment.delivery_fee || ""}
										onChange={(e) =>
											setNewShipment((prev) => ({
												...prev,
												delivery_fee: parseFloat(e.target.value) || undefined,
											}))
										}
									/>
								</div>
							)}

							<div className="flex flex-col space-y-2 w-full">
								<div className="space-y-2 w-full">
									<Label htmlFor="extra_charge">Extra Charge ($)</Label>
									<Input
										id="extra_charge"
										type="number"
										value={newShipment.extra_charge || ""}
										onChange={(e) =>
											setNewShipment((prev) => ({
												...prev,
												extra_charge: parseFloat(e.target.value) || undefined,
											}))
										}
									/>
								</div>
							</div>
						</div>

						<div>
							<Label>Discounts & Promo Codes</Label>

							<RSelect
								isMulti
								isDisabled={!newShipment.customer_id}
								options={[
									...activePromoCodes.map((c) => ({
										...c,
										isDiscount: false,
									})),
									...activeDiscounts.map((c) => ({ ...c, isDiscount: true })),
								]
									.filter((code) => {
										if (selectedCodes.length) {
											if (!code.is_stackable) return false;

											const firstCode = [
												...activePromoCodes,
												...activeDiscounts,
											].find((apc) => apc.id === selectedCodes[0].value);

											if (!firstCode?.is_stackable) return false;
										}

										const customerUsage = [
											...customerPromoUsages,
											...customerDiscountUsages,
										].find((usage) => usage.code_id === code.id);

										// if it's a discount and there's no customer usage
										// it means this customer isn't allowed to use this discount
										if (code.isDiscount && !customerUsage) return false;

										// If customer hasn't used the code at all
										// or the code has no limit per customer
										if (!customerUsage || !code.usage_limit_per_customer)
											return true;

										// If customer has reached their limit for this usage already
										if (
											customerUsage?.usage_count &&
											customerUsage.usage_count >= code.usage_limit_per_customer
										) {
											if (!code.isDiscount) return false;

											// cast customerUsage to DiscountCustomerUsage
											const discountUsage =
												customerUsage as DiscountCustomerUsage;

											// If it's a discount, check on the customer's individual usage limit
											if (
												discountUsage?.usage_limit &&
												discountUsage?.usage_count! >=
												discountUsage?.usage_limit
											)
												return false;
										}

										return true;
									})
									.reduce(
										(final, curr) => {
											let group: {
												label: string;
												options: { value: string; label: string }[];
											};

											if (curr.isDiscount) {
												group = final[0];
												group.options.push({
													value: curr.id,
													label: curr.code,
												});
											} else {
												group = final[1];
												group.options.push({
													value: curr.id,
													label: curr.code,
												});
											}

											return final;
										},
										[
											{
												label: "Discounts",
												options: [],
											},
											{
												label: "Promo Codes",
												options: [],
											},
										]
									)}
								closeMenuOnSelect={false}
								value={selectedCodes}
								onChange={(selected) => {
									setSelectedCodes(selected);
								}}
								placeholder="Select one or more codes"
							/>

							{!newShipment.customer_id && (
								<span className="text-xs text-muted-foreground">
									Please select a customer first
								</span>
							)}
						</div>

						<div className="space-y-2">
							<div className="space-x-2 items-center flex">
								<Checkbox
									disabled={!newShipment.total_cost}
									id="is_customs_taxes_included"
									checked={isCustomsTaxesIncluded}
									onCheckedChange={(checked: boolean) =>
										setIsCustomsTaxesIncluded(checked)
									}
								/>
								<Label htmlFor="is_customs_taxes_included">
									Include Customs Taxes
								</Label>
							</div>

							{newShipment.total_cost > 0 && isCustomsTaxesIncluded && (
								<div className="flex gap-4">
									<div>
										<Label>Customs Tax Type</Label>

										<Select
											value={newShipment.customs_tax_type!}
											onValueChange={(value: "percentage" | "fixed") =>
												setNewShipment((prev) => ({
													...prev,
													customs_tax_type: value,
												}))
											}
										>
											<SelectTrigger>
												<SelectValue />
											</SelectTrigger>
											<SelectContent>
												<SelectItem value="percentage">Percentage</SelectItem>
												<SelectItem value="fixed">Fixed</SelectItem>
											</SelectContent>
										</Select>
									</div>

									<div>
										<Label htmlFor="customs_tax_rate">Customs Tax Rate</Label>
										<Input
											id="customs_tax_rate"
											type="number"
											value={newShipment.customs_tax_rate || ""}
											onChange={(e) =>
												setNewShipment((prev) => ({
													...prev,
													customs_tax_rate:
														parseFloat(e.target.value) || undefined,
												}))
											}
										/>
									</div>
								</div>
							)}

							{!newShipment.total_cost && (
								<span className="text-xs text-muted-foreground">
									Please fill out package details to calculate cost first
								</span>
							)}
						</div>

						{/* TODO: Display the pricing tier (air method) or the sea rate being used */}

						<div className="space-y-2">
							<Label>Total Cost</Label>
							<div className="text-2xl font-bold text-[#254164]">
								${newShipment.total_cost!.toFixed(2)}
							</div>

							<CostBreakdown
								shipment={newShipment}
								shipmentDiscounts={shipmentDiscounts}
							/>
						</div>
					</CardContent>
				</Card>
			</div>

			<DropdownMenu>
				<DropdownMenuTrigger asChild>
					<Button className="w-full">Create Shipment</Button>
				</DropdownMenuTrigger>
				<DropdownMenuContent className="w-56" align="start">
					<DropdownMenuLabel className="text-lg">
						Select a Status
					</DropdownMenuLabel>

					<DropdownMenuItem onClick={() => handleCreateShipment("estimated")}>
						<div className="flex flex-col">
							<div>Estimated</div>
							<p className="text-xs text-muted-foreground">
								The shipment's cost has been calculated. This will send a
								confirmation request to the customer.
							</p>
						</div>
					</DropdownMenuItem>

					<DropdownMenuSub>
						<DropdownMenuSubTrigger>
							<div className="flex flex-col">
								<div>Confirmed</div>
								<p className="text-xs text-muted-foreground">
									The customer has approved the final cost, or you have approved
									it and can proceed directly.
								</p>
							</div>
						</DropdownMenuSubTrigger>

						<DropdownMenuPortal>
							<DropdownMenuSubContent>
								{newShipment.is_pickup_requested && (
									<DropdownMenuItem
										onClick={() =>
											handleCreateShipment("picking_up_from_sender")
										}
									>
										<div className="flex flex-col">
											<div>Picking Up</div>
											<p className="text-xs text-muted-foreground">
												We're already on our way to pick up the package from the
												sender's address.
											</p>
										</div>
									</DropdownMenuItem>
								)}

								<div className="flex flex-col">
									<DropdownMenuItem
										onClick={() =>
											handleCreateShipment("received_at_warehouse")
										}
									>
										<div className="flex flex-col">
											<div>Received</div>
											<p className="text-xs text-muted-foreground">
												{newShipment.is_pickup_requested
													? "The package has been picked up from the sender's address"
													: "The sender has already brought in or delivered the package at our warehouse."}
											</p>
										</div>
									</DropdownMenuItem>
								</div>

								<DropdownMenuSeparator />

								<DropdownMenuItem
									onClick={() => handleCreateShipment("confirmed")}
								>
									<div className="flex flex-col">
										<div>Pending...</div>
										<p className="text-xs text-muted-foreground">
											{newShipment.is_pickup_requested
												? "Waiting for the pickup to be scheduled."
												: "Waiting for the sender to bring in or deliver the package at our warehouse."}
										</p>
									</div>
								</DropdownMenuItem>
							</DropdownMenuSubContent>
						</DropdownMenuPortal>
					</DropdownMenuSub>
				</DropdownMenuContent>
			</DropdownMenu>
		</div>
	);
}
